/**
 * Auth Admin get-* API client.
 * All endpoints require Authorization: Bearer <accessToken> (from Login Verify).
 * Paths follow convention; adjust to match deployed API Gateway.
 */

import { requestAuthAdmin, parseResponse } from './auth';

/** GET /api-admin/v0/users?page=&page_size=&search=&exclude_resigned=&banned_only= */
export async function getUsers(page = 1, pageSize = 10, accessToken, search = '', options = {}) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  const q = typeof search === 'string' ? search.trim() : '';
  if (q) params.set('search', q);
  if (options.bannedOnly) {
    params.set('banned_only', '1');
  } else if (options.excludeResigned) {
    params.set('exclude_resigned', '1');
  }
  const res = await requestAuthAdmin(`/api-admin/v0/users?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/users');
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/summary */
export async function getUsersSummary(accessToken) {
  const res = await requestAuthAdmin('/api-admin/v0/users/summary', { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/users/summary');
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/by-id?user_id= or path */
export async function getUserById(userId, accessToken) {
  const res = await requestAuthAdmin(
    `/api-admin/v0/users/by-id?user_id=${encodeURIComponent(userId)}`,
    { method: 'GET' },
    accessToken
  );
  const data = await parseResponse(res, '/api-admin/v0/users/by-id');
  return data?.data ?? data;
}

/** Parallel getUserById for unique ids. Missing/failed ids are omitted. */
export async function getUsersByIds(userIds, accessToken) {
  const unique = [...new Set((userIds || []).map((id) => Number(id)).filter((id) => Number.isFinite(id) && id > 0))];
  const out = {};
  const chunkSize = 8;
  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);
    const rows = await Promise.all(
      chunk.map((id) => getUserById(id, accessToken).catch(() => null)),
    );
    rows.forEach((row, idx) => {
      if (!row) return;
      out[chunk[idx]] = row;
    });
  }
  return out;
}

/** GET /api-admin/v0/celebs?page=&page_size=&search=&exclude_resigned=&is_celeb= */
export async function getCelebs(page = 1, pageSize = 10, accessToken, search = '', options = {}) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  const q = typeof search === 'string' ? search.trim() : '';
  if (q) params.set('search', q);
  if (options.excludeResigned) {
    params.set('exclude_resigned', '1');
  }
  if (options.isCeleb === 1 || options.isCeleb === 2) {
    params.set('is_celeb', String(options.isCeleb));
  }
  const res = await requestAuthAdmin(`/api-admin/v0/celebs?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/celebs');
  return data?.data ?? data;
}

/** GET /api-admin/v0/celebs/bubbles?page=&page_size=&search=&message=&exclude_resigned=&status= */
export async function getCelebBubbles(page = 1, pageSize = 10, accessToken, options = {}) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  const search = typeof options.search === 'string' ? options.search.trim() : '';
  if (search) params.set('search', search);
  const message = typeof options.message === 'string' ? options.message.trim() : '';
  if (message) params.set('message', message);
  if (options.excludeResigned) {
    params.set('exclude_resigned', '1');
  }
  if (options.status === 1 || options.status === 2 || options.status === 3) {
    params.set('status', String(options.status));
  }
  const res = await requestAuthAdmin(`/api-admin/v0/celebs/bubbles?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/celebs/bubbles');
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/fans?user_id=&page=&page_size= */
export async function getUserFans(userId, page = 1, pageSize = 10, accessToken) {
  const params = new URLSearchParams({
    user_id: String(userId),
    page: String(page),
    page_size: String(pageSize),
  });
  const res = await requestAuthAdmin(`/api-admin/v0/users/fans?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/users/fans');
  return data?.data ?? data;
}

/** POST /api-admin/v0/users/partner-celeb — set is_celeb=2 (partner celeb). */
export async function registerPartnerCeleb(userId, accessToken) {
  const res = await requestAuthAdmin(
    '/api-admin/v0/users/partner-celeb',
    {
      method: 'POST',
      body: JSON.stringify({ user_id: Number(userId) }),
    },
    accessToken,
  );
  const data = await parseResponse(res, '/api-admin/v0/users/partner-celeb');
  return data?.data ?? data;
}

/** DELETE /api-admin/v0/users/partner-celeb — set is_celeb=0 (unregister partner celeb). */
export async function unregisterPartnerCeleb(userId, accessToken) {
  const res = await requestAuthAdmin(
    '/api-admin/v0/users/partner-celeb',
    {
      method: 'DELETE',
      body: JSON.stringify({ user_id: Number(userId) }),
    },
    accessToken,
  );
  const data = await parseResponse(res, '/api-admin/v0/users/partner-celeb');
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/tickets?user_id= */
export async function getUserTickets(userId, accessToken) {
  const res = await requestAuthAdmin(
    `/api-admin/v0/users/tickets?user_id=${encodeURIComponent(userId)}`,
    { method: 'GET' },
    accessToken
  );
  const data = await parseResponse(res, '/api-admin/v0/users/tickets');
  return data?.data ?? data;
}

/** GET /api-admin/v0/reports?page=&page_size= */
export async function getReports(page = 1, pageSize = 10, accessToken) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  const res = await requestAuthAdmin(`/api-admin/v0/reports?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/reports');
  return data?.data ?? data;
}

export async function getModerationCases(page = 1, pageSize = 20, accessToken, status = '', priority = '') {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (status !== '') params.set('status', String(status));
  if (priority !== '') params.set('priority', String(priority));
  const path = `/api-admin/v0/moderation/cases?${params}`;
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationOverview(accessToken) {
  const path = '/api-admin/v0/moderation/overview';
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationPriorityRules(accessToken) {
  const path = '/api-admin/v0/moderation/priority-rules';
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function updateModerationPriorityRules(items, accessToken) {
  const path = '/api-admin/v0/moderation/priority-rules';
  const requestId = crypto.randomUUID();
  const res = await requestAuthAdmin(path, {
    method: 'PUT',
    headers: { 'Idempotency-Key': requestId },
    body: JSON.stringify({ items }),
  }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationUsers(page = 1, pageSize = 20, search = '', accessToken) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (search.trim()) params.set('search', search.trim());
  const path = `/api-admin/v0/moderation/users?${params}`;
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationStories(page = 1, pageSize = 20, sort = 'latest', accessToken) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize), sort });
  const path = `/api-admin/v0/moderation/stories?${params}`;
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

function speechCursorParams(cursor, limit = 50, extra = {}) {
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('before_id', String(cursor));
  Object.entries(extra).forEach(([key, value]) => {
    const normalized = typeof value === 'string' ? value.trim() : value;
    if (normalized !== '' && normalized != null) params.set(key, String(normalized));
  });
  return params;
}

export async function getModerationSpeech({ cursor, limit = 50, planetId, spatialCell } = {}, accessToken) {
  const params = speechCursorParams(cursor, limit, { planet_id: planetId, spatial_cell: spatialCell });
  const path = `/api-admin/v0/moderation/speech?${params}`;
  const res = await requestAuthAdmin(path, { method: 'GET', headers: { 'X-Moderation-Purpose': 'TEXT_CHAT_BROWSE' } }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationSpeechReports({ cursor, limit = 50 } = {}, accessToken) {
  const params = speechCursorParams(cursor, limit);
  const path = `/api-admin/v0/moderation/speech/reports?${params}`;
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationSpeechCases({ cursor, limit = 50 } = {}, accessToken) {
  const params = speechCursorParams(cursor, limit);
  const path = `/api-admin/v0/moderation/speech/cases?${params}`;
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

/**
 * FB-1167 contract for the case-oriented Speech workspace.
 * The legacy raw-report read remains available independently of this queue.
 */
export async function getModerationSpeechCase(caseId, purpose, accessToken) {
  if (!String(purpose || '').trim()) throw new Error('Moderation purpose is required');
  const path = `/api-admin/v0/moderation/speech/cases/${encodeURIComponent(caseId)}`;
  const res = await requestAuthAdmin(path, {
    method: 'GET',
    headers: { 'X-Moderation-Purpose': String(purpose).trim() },
  }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function applyModerationSpeechCaseAction(caseId, action, input = {}, accessToken) {
  if (!['dismiss', 'remove'].includes(action)) throw new Error('Unsupported Speech moderation action');
  if (input.expected_content_revision == null || input.expected_content_revision === '' || !Number.isInteger(Number(input.expected_content_revision))) throw new Error('expected_content_revision is required');
  if (!String(input.reason_code || '').trim()) throw new Error('reason_code is required');
  const path = `/api-admin/v0/moderation/speech/cases/${encodeURIComponent(caseId)}/${action}`;
  const requestId = input.request_id || crypto.randomUUID();
  const body = { ...input };
  delete body.request_id;
  const res = await requestAuthAdmin(path, {
    method: 'POST',
    headers: { 'Idempotency-Key': requestId },
    body: JSON.stringify(body),
  }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function setModerationSpeechChatRestriction(userId, duration, input = {}, accessToken) {
  if (!['10m', '1h', '24h'].includes(duration)) throw new Error('Unsupported Chat restriction duration');
  if (input.source_case_id != null && (input.source_case_id === '' || !Number.isInteger(Number(input.source_case_id)) || Number(input.source_case_id) < 1)) throw new Error('source_case_id must be a positive integer');
  if (!String(input.reason_code || '').trim()) throw new Error('reason_code is required');
  const path = `/api-admin/v0/moderation/users/${encodeURIComponent(userId)}/chat-restriction`;
  const requestId = input.request_id || crypto.randomUUID();
  const body = { ...input };
  delete body.request_id;
  const res = await requestAuthAdmin(path, {
    method: 'POST',
    headers: { 'Idempotency-Key': requestId },
    body: JSON.stringify({ ...body, duration }),
  }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function liftModerationSpeechChatRestriction(userId, input = {}, accessToken) {
  if (input.restriction_id == null || input.restriction_id === '' || !Number.isInteger(Number(input.restriction_id))) throw new Error('restriction_id is required');
  if (!String(input.reason_code || '').trim()) throw new Error('reason_code is required');
  const path = `/api-admin/v0/moderation/users/${encodeURIComponent(userId)}/chat-restriction`;
  const requestId = input.request_id || crypto.randomUUID();
  const body = { ...input };
  delete body.request_id;
  const res = await requestAuthAdmin(path, {
    method: 'DELETE',
    headers: { 'Idempotency-Key': requestId },
    body: JSON.stringify(body),
  }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationSpeechByUser(userId, { cursor, limit = 50 } = {}, accessToken) {
  const params = speechCursorParams(cursor, limit);
  const path = `/api-admin/v0/moderation/speech/users/${encodeURIComponent(userId)}?${params}`;
  const res = await requestAuthAdmin(path, { method: 'GET', headers: { 'X-Moderation-Purpose': 'USER_UGC_REVIEW' } }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationUser(userId, accessToken, { contentType = "ALL", page = 1, pageSize = 20, includeUGC = false } = {}) {
  const params = new URLSearchParams({ include_ugc: '1', content_type: contentType, ugc_page: String(page), ugc_page_size: String(pageSize) });
  const path = `/api-admin/v0/moderation/users/${encodeURIComponent(userId)}${includeUGC ? `?${params}` : ''}`;
  const res = await requestAuthAdmin(path, { method: 'GET', ...(includeUGC ? { headers: { 'X-Moderation-Purpose': 'USER_UGC_REVIEW' } } : {}) }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function startModerationStoryReview(storyId, input, accessToken) {
  const path = `/api-admin/v0/moderation/stories/${encodeURIComponent(storyId)}/start-review`;
  const requestId = crypto.randomUUID();
  const res = await requestAuthAdmin(path, {
    method: 'POST',
    headers: { 'Idempotency-Key': requestId },
    body: JSON.stringify(input),
  }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function getModerationCase(caseId, accessToken) {
  const path = `/api-admin/v0/moderation/cases/${encodeURIComponent(caseId)}`;
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

export async function applyModerationAction(caseId, action, input, accessToken) {
  const path = `/api-admin/v0/moderation/cases/${encodeURIComponent(caseId)}/${action}`;
  const requestId = crypto.randomUUID();
  const res = await requestAuthAdmin(
    path,
    {
      method: 'POST',
      headers: { 'Idempotency-Key': requestId },
      body: JSON.stringify({ ...input, request_id: requestId }),
    },
    accessToken,
  );
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/devices?user_id=&page=&page_size= */
export async function getUserDevices(userId, page = 1, pageSize = 10, accessToken) {
  const params = new URLSearchParams({
    user_id: String(userId),
    page: String(page),
    page_size: String(pageSize),
  });
  const res = await requestAuthAdmin(`/api-admin/v0/users/devices?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/users/devices');
  return data?.data ?? data;
}

/** GET /api-admin/v0/blocks?user_id=&page=&page_size= */
export async function getUserBlocks(userId, page = 1, pageSize = 10, accessToken) {
  const params = new URLSearchParams({
    user_id: String(userId),
    page: String(page),
    page_size: String(pageSize),
  });
  const res = await requestAuthAdmin(`/api-admin/v0/blocks?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/blocks');
  return data?.data ?? data;
}

/** GET /api-admin/v0/likes?user_id=&page=&page_size= (likes sent by user) */
export async function getUserLikes(userId, page = 1, pageSize = 10, accessToken) {
  const params = new URLSearchParams({
    user_id: String(userId),
    page: String(page),
    page_size: String(pageSize),
  });
  const res = await requestAuthAdmin(`/api-admin/v0/likes?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/likes');
  return data?.data ?? data;
}

/** GET /api-admin/v0/likes/received?user_id=&page=&page_size= (likes received by user) */
export async function getUserLikesReceived(userId, page = 1, pageSize = 10, accessToken) {
  const params = new URLSearchParams({
    user_id: String(userId),
    page: String(page),
    page_size: String(pageSize),
  });
  const res = await requestAuthAdmin(`/api-admin/v0/likes/received?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/likes/received');
  return data?.data ?? data;
}

/** GET /api-admin/v0/signups/timeseries?start_date=&end_date= (YYYY-MM-DD) */
export async function getSignupsTimeseries(startDate, endDate, accessToken) {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const qs = params.toString();
  const res = await requestAuthAdmin(
    `/api-admin/v0/signups/timeseries${qs ? `?${qs}` : ''}`,
    { method: 'GET' },
    accessToken
  );
  const data = await parseResponse(res, '/api-admin/v0/signups/timeseries');
  return data?.data ?? data;
}

/** GET /api-admin/v0/signups/by-month?start_month=&end_month= (YYYY-MM) */
export async function getSignupsByMonth(startMonth, endMonth, accessToken) {
  const params = new URLSearchParams();
  if (startMonth) params.set('start_month', startMonth);
  if (endMonth) params.set('end_month', endMonth);
  const qs = params.toString();
  const res = await requestAuthAdmin(
    `/api-admin/v0/signups/by-month${qs ? `?${qs}` : ''}`,
    { method: 'GET' },
    accessToken
  );
  const data = await parseResponse(res, '/api-admin/v0/signups/by-month');
  return data?.data ?? data;
}

/** GET /api-admin/v0/dashboard/overview */
export async function getDashboardOverview(accessToken) {
  const res = await requestAuthAdmin('/api-admin/v0/dashboard/overview', { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/dashboard/overview');
  return data?.data ?? data;
}

/** GET /api-admin/v0/dashboard/activity-timeseries?start_date=&end_date= (YYYY-MM-DD) */
export async function getDashboardActivityTimeseries(startDate, endDate, accessToken) {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  const qs = params.toString();
  const res = await requestAuthAdmin(
    `/api-admin/v0/dashboard/activity-timeseries${qs ? `?${qs}` : ''}`,
    { method: 'GET' },
    accessToken
  );
  const data = await parseResponse(res, '/api-admin/v0/dashboard/activity-timeseries');
  return data?.data ?? data;
}

/** GET /api-admin/v0/signups/total */
export async function getTotalSignupUsers(accessToken) {
  const res = await requestAuthAdmin('/api-admin/v0/signups/total', { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/signups/total');
  return data?.data ?? data;
}

/** GET /api-admin/v0/devices/registration-summary */
export async function getDevicesRegistrationSummary(accessToken) {
  const res = await requestAuthAdmin(
    '/api-admin/v0/devices/registration-summary',
    { method: 'GET' },
    accessToken
  );
  const data = await parseResponse(res, '/api-admin/v0/devices/registration-summary');
  return data?.data ?? data;
}

/** GET /api-admin/v0/devices/distribution?group_by=app_version|app_platform */
export async function getDevicesDistribution(groupBy, accessToken) {
  const params = new URLSearchParams({ group_by: String(groupBy) });
  const res = await requestAuthAdmin(
    `/api-admin/v0/devices/distribution?${params}`,
    { method: 'GET' },
    accessToken
  );
  const data = await parseResponse(res, '/api-admin/v0/devices/distribution');
  return data?.data ?? data;
}

/** POST /api-admin/v2/users/ban — admin ban a USER account (account_inactive_reason=3). */
export async function banUser(userId, accessToken) {
  const res = await requestAuthAdmin(
    '/api-admin/v2/users/ban',
    {
      method: 'POST',
      body: JSON.stringify({ user_id: Number(userId) }),
    },
    accessToken,
  );
  const data = await parseResponse(res, '/api-admin/v2/users/ban');
  return data?.data ?? data;
}

/** DELETE /api-admin/v2/users/ban — restore an admin-banned USER account. */
export async function unbanUser(userId, accessToken) {
  const res = await requestAuthAdmin(
    '/api-admin/v2/users/ban',
    {
      method: 'DELETE',
      body: JSON.stringify({ user_id: Number(userId) }),
    },
    accessToken,
  );
  const data = await parseResponse(res, '/api-admin/v2/users/ban');
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/ban-logs?page=&page_size=&action=&search=&user_id= */
export async function getUserBanLogs(page = 1, pageSize = 10, accessToken, options = {}) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (options.action === 'ban' || options.action === 'unban') {
    params.set('action', options.action);
  }
  if (options.userId != null && options.userId !== '') {
    params.set('user_id', String(options.userId));
  }
  const q = typeof options.search === 'string' ? options.search.trim() : '';
  if (q) params.set('search', q);
  const res = await requestAuthAdmin(`/api-admin/v0/users/ban-logs?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/users/ban-logs');
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/account?user_id= — accounts_beta row (null if missing). */
export async function getUserAccount(userId, accessToken) {
  const params = new URLSearchParams({ user_id: String(userId) });
  const res = await requestAuthAdmin(`/api-admin/v0/users/account?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/users/account');
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/notifications?user_id= — notifications_beta row (null if missing). */
export async function getUserNotifications(userId, accessToken) {
  const params = new URLSearchParams({ user_id: String(userId) });
  const res = await requestAuthAdmin(`/api-admin/v0/users/notifications?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/users/notifications');
  return data?.data ?? data;
}

/** PATCH /api-admin/v0/users/account-settings — partial accounts_beta / notifications_beta toggles. */
export async function patchUserAccountSettings(userId, fields, accessToken) {
  const res = await requestAuthAdmin(
    '/api-admin/v0/users/account-settings',
    {
      method: 'PATCH',
      body: JSON.stringify({ user_id: Number(userId), ...fields }),
    },
    accessToken
  );
  const data = await parseResponse(res, '/api-admin/v0/users/account-settings');
  return data?.data ?? data;
}

/** PATCH /api-admin/v0/users/account-type — update one service user's account_type. */
export async function patchUserAccountType(userId, accountType, accessToken) {
  const path = '/api-admin/v0/users/account-type';
  const res = await requestAuthAdmin(
    path,
    {
      method: 'PATCH',
      body: JSON.stringify({ user_id: Number(userId), account_type: accountType }),
    },
    accessToken
  );
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}

/** GET /api-admin/v0/users/links?user_id= — platform and custom links registered by user. */
export async function getUserLinks(userId, accessToken) {
  const params = new URLSearchParams({ user_id: String(userId) });
  const res = await requestAuthAdmin(`/api-admin/v0/users/links?${params}`, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, '/api-admin/v0/users/links');
  return data?.data ?? data;
}

/** Active internal Admin Web account; suspension is separate from Chat restriction. */
export async function getUserSuspension(userId, accessToken) {
  const path = `/api-admin/v0/users/${Number(userId)}/suspension`;
  const res = await requestAuthAdmin(path, { method: 'GET' }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}
export async function changeUserSuspension(userId, action, input, requestId, accessToken) {
  const version = action === 'lift' ? 'v0' : 'v1';
  const path = `/api-admin/${version}/users/${Number(userId)}/suspension`;
  const res = await requestAuthAdmin(path, {
    method: action === 'lift' ? 'DELETE' : 'POST',
    headers: { 'Idempotency-Key': requestId },
    body: JSON.stringify(input),
  }, accessToken);
  const data = await parseResponse(res, path);
  return data?.data ?? data;
}
